<template>
  <div class="invest container">
    <div>
      <loader v-show="isLoading"/>
      <div v-if="!isLoading">
        <div class="row w-100">
          <before-form-header v-if="!gotResponse" class="header-table" :title="$t('views.confirm-investment.confirmInvestment')" :description="'views.confirm-investment.headerDescr'"/>
          <before-form-header v-if="gotResponse" class="header-table" :title="$t('views.confirm-investment.investmentConfirmation')" description=""/>
          <p v-if="!gotResponse" class="small-notification-text" :class="{'small-notification-text-closer': gotResponse}">{{ $t('views.confirm-investment.investmentsAreAlso') }}</p>
          <div class="w-100">
            <error :errors="errors"></error>
          </div>
          <div class="income-box w-100" v-if="!gotResponse">
            <b-table hover
                     :items="filteredItems"
                     :fields="tableFields"
                     class="confirm-invest-table invest-table income-table w-100"
                     responsive="lg">
              <template v-slot:table-busy>
                <div class="text-center text-danger my-2">
                  <loader/>
                </div>
              </template>
              <template v-slot:head()="data">
                <span class="table-header">{{ $t(data.label) }}</span>
              </template>
              <template v-slot:cell(id)="{ item }">
                <router-link :to="{ path: '/loan/'+ item.loan_ref }">{{ item.loan_ref }} - {{ item.loan_type }}</router-link>
              </template>
              <template v-slot:cell(originator)="{ item }">
                {{ item.originator_name ? item.originator_name : item.loan_originator_name }}
              </template>
              <template v-slot:cell(country)="{ value }">
                <img class="flag" :src="getFlag(value)" alt=""/>
              </template>
              <template v-slot:cell(available_amount)="{ item }">
                {{ item.available_amount ? item.available_amount : item.investable_amount | moneyFormatter }}
              </template>
              <template v-slot:cell(loan_issued_amount)="{ item }">
                {{ item.loan_saldo ? item.loan_saldo : (item.loan_issued_amount_in_eur ? item.loan_issued_amount_in_eur : item.loan_issued_amount) | moneyFormatter }}
              </template>
              <template v-slot:cell(lo_assignment_agreement_link)="{ item }">
                <div class="text-center">
                  <a v-if="item.lo_assignment_agreement_link" target="_blank" :href="item.lo_assignment_agreement_link">
                    <img src="../assets/pdf.svg" alt="Assignment agreement"/>
                  </a>
                </div>
              </template>
              <template v-slot:cell(loan_due_date)="{ value }">
                {{ showDiffInTermFormat(value, new Date()) }}
              </template>
              <template v-slot:cell(loan_term_period)="{ item }">
                {{ showDiffInTermFormat(item.loan_due_date, new Date().toDateString()) }}
              </template>
              <template v-slot:cell(amount)="row">
                <div class="d-flex align-items-center">
                  <div>
                    <b-input-group size="sm" class="invest-ig">
                      <b-input-group-prepend class="prepend">
                        €
                      </b-input-group-prepend>
                      <b-form-input
                          @input="validate($event, row.item.loan_ref, row.item.investable_amount)"
                          v-bind:ref="row.item.loan_ref"
                          v-bind:value="row.item.amount"
                          type="number" step="any"
                          v-bind:min="getMinAmount(row.item.investable_amount)"
                          v-bind:max="parseFloat(row.item.investable_amount)"
                          @change="updatedInvests[row.item.loan_ref] = maxRound($event, row.item.investable_amount, row.item.loan_ref); updateInvest(row.item)"
                          class="invest-input"
                          @keypress="isNumber($event)"
                          v-on:keyup.enter="updateInvest(row.item)">
                      </b-form-input>
                      <b-input-group-append class="append" @click="updateInvest(row.item)">
                        <img v-bind:src="getSrc('portfolio.svg')" alt=""/>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                  <div>
                    <b-input-group-text class="remove-invest" @click="removeInvest(row.item.loan_ref)">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.16432 14.6667C7.26098 14.6667 6.38032 14.6567 5.50898 14.6387C4.39432 14.6167 3.62298 13.894 3.49698 12.7527C3.28698 10.8593 2.92765 6.39667 2.92432 6.352C2.90165 6.07667 3.10698 5.83534 3.38232 5.81334C3.65365 5.806 3.89898 5.99667 3.92098 6.27134C3.92432 6.31667 4.28298 10.764 4.49098 12.6427C4.56232 13.2913 4.91232 13.626 5.52965 13.6387C7.19632 13.674 8.89698 13.676 10.7303 13.6427C11.3863 13.63 11.741 13.302 11.8143 12.638C12.021 10.7753 12.381 6.31667 12.385 6.27134C12.407 5.99667 12.6503 5.80467 12.923 5.81334C13.1983 5.836 13.4037 6.07667 13.3816 6.352C13.3777 6.39734 13.0163 10.8713 12.8083 12.748C12.679 13.9127 11.9097 14.6213 10.7483 14.6427C9.85965 14.658 9.00232 14.6667 8.16432 14.6667Z" fill="#0C2D45"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13.8053 4.65953H2.5C2.224 4.65953 2 4.43553 2 4.15953C2 3.88353 2.224 3.65953 2.5 3.65953H13.8053C14.0813 3.65953 14.3053 3.88353 14.3053 4.15953C14.3053 4.43553 14.0813 4.65953 13.8053 4.65953Z" fill="#0C2D45"/>
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M11.6269 4.65953C10.8682 4.65953 10.2096 4.11887 10.0602 3.37487L9.89822 2.5642C9.86422 2.44087 9.72356 2.33353 9.56356 2.33353H6.74156C6.58156 2.33353 6.44089 2.44087 6.40022 2.59487L6.24489 3.37487C6.09622 4.11887 5.43689 4.65953 4.67822 4.65953C4.40222 4.65953 4.17822 4.43553 4.17822 4.15953C4.17822 3.88353 4.40222 3.65953 4.67822 3.65953C4.96222 3.65953 5.20889 3.45687 5.26489 3.1782L5.42689 2.36753C5.59156 1.7462 6.12956 1.33353 6.74156 1.33353H9.56356C10.1756 1.33353 10.7136 1.7462 10.8716 2.33753L11.0409 3.1782C11.0962 3.45687 11.3429 3.65953 11.6269 3.65953C11.9029 3.65953 12.1269 3.88353 12.1269 4.15953C12.1269 4.43553 11.9029 4.65953 11.6269 4.65953Z" fill="#0C2D45"/>
                      </svg>
                    </b-input-group-text>
                  </div>
                </div>
              </template>
            </b-table>
          </div>
          <div class="w-100">
            <b-alert show v-if="gotResponse" :variant="responseType">{{ $t(responseResult) }}</b-alert>
          </div>
          <div class="income-box w-100" v-if="gotResponse">
            <b-table hover :items="responseItems" :fields="responseFields" class="confirm-invest-table invest-table income-table w-100" responsive="lg">
            <template v-slot:head()="data">
              <span class="table-header">{{ $t(data.label) }}</span>
            </template>
            <template v-slot:cell(id)="{ item }">
              <router-link :to="{ path: '/loan/'+ item.id }">{{ item.id }}</router-link>
            </template>
            <template v-slot:cell(tried_to_invest_amount)="{ value }">
              {{ value | moneyFormatter }}
            </template>
            <template v-slot:cell(invested_amount)="{ value }">
              {{ value | moneyFormatter }}
            </template>
            <template v-slot:cell(message)="{ item }">
              <b-alert show v-bind:variant="item.invested_amount?'success' : 'warning'">{{ $t(item.message_i18n) }}</b-alert>
            </template>
          </b-table>
          </div>
          <div class="row w-100 row-no-padding">
            <div class="col-12 col-md-6"></div>
            <div class="col-12 col-md-6">

              <div class="summary" v-if="!gotResponse">
                <div class="income-box w-100">
                  <div class="details">
                    <div>{{ $t('views.confirm-investment.number') }} <span class="value">{{ investCounter }}</span></div>
                    <div>{{ $t('views.confirm-investment.totalAmount') }} <span class="value"> {{ totalInvestAmount | moneyFormatter }}</span>
                    </div>
                  </div>
                </div>
                <div class="w-100">
                  <b-button class="remove-all" @click="removeAll()" v-if="investCounter">{{ $t('views.confirm-investment.removeAll') }}</b-button>
                  <b-button @click="submitConfirm()" class="confirm-invest-btn" variant="light" v-if="investCounter">{{ $t('views.confirm-investment.confirm') }}</b-button>
                </div>
              </div>
              <div class="summary" v-if="gotResponse">
                <div class="income-box w-100">
                  <div class="confirm-invest-survey-btn-container">
                    <a href="https://www.surveymonkey.com/r/JLSHCZQ" target="_blank" class="confirm-invest-survey">{{ $t('views.confirm-investment.takeASurvey') }}</a>
                    <b-button @click="$router.push('/investments')" class="confirm-invest-btn" variant="light">{{ $t('views.confirm-investment.goToInvestments') }}</b-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BeforeFormHeader from '../components/BeforeFormHeader'
import moneyMixin from '../mixins/money'
import flagMixin from '../mixins/flag'
import filtersMixin from '../mixins/filters'
import imageMixin from '../mixins/image'
import investConfig from '../config/invests.json'
import investorInstance from '../config/investor.json'
import dateMixin from '../mixins/date'
import Loader from '@/components/Loader'
import loadingMixin from '../mixins/loading'
import Error from '@/components/Error'
import investor from '@/api/investor'
import trackingHelper from '@/helpers/trackingHelper'

export default {
  components: {Error, Loader, BeforeFormHeader},
  mixins: [moneyMixin, flagMixin, filtersMixin, imageMixin, dateMixin, loadingMixin],
  data()
  {
    return {
      tableFields: [
        {key: 'country', label: '', class: 'table-header'},
        {key: 'id', label: 'views.invest.table.id', class: 'table-header'},
        {key: 'originator', label: 'views.invest.table.originator', class: 'table-header'},
        {key: 'loan_investor_interest_rate', label: 'views.invest.table.interest', formatter: 'showAsPercentages', class: 'table-header'},
        {key: 'loan_term_period', label: 'views.createautoinvest.table.term', class: 'table-header'},
        {key: 'loan_issued_amount', label: 'views.invest.table.loanAmount', class: 'table-header'},
        {key: 'available_amount', label: 'views.invest.table.availableAmount', class: 'table-header'},
        {key: 'lo_assignment_agreement_link', label: 'views.investments.table.assignmentAgreement', class: 'table-header text-center'},
        // {key: 'invest', label: '', class: 'table-header'},
        {key: 'amount', label: '', class: 'table-header'}
      ],
      updatedInvests: [],
      investor: investorInstance,
      investConfig: investConfig,
      isLoading: true,
      debugInvestments: null,
      errors: [],
      gotResponse: false,
      responseResult: '',
      responseType: 'success',
      responseItems: [],
      responseFields: [
        {key: 'id', label: 'views.invest.table.id', class: 'table-header'},
        {key: 'loan_originator_name', label: 'views.confirm-investment.originatorName', class: 'table-header'},
        {key: 'tried_to_invest_amount', label: 'views.confirm-investment.triedToInvest', class: 'table-header'},
        {key: 'invested_amount', label: 'views.confirm-investment.investedAmount', class: 'table-header'},
        {key: 'message', label: 'views.confirm-investment.resultMessage', class: 'table-header'}
      ],
      minAmount: investConfig.minLimitAmount
    }
  },
  computed: {
    filteredItems()
    {
      const investItems = this.$store.getters['invests/getInvestItems']
      investItems.forEach(function (investItem)
      {
        if (this.$refs[investItem.loan_ref])
        {
          this.$refs[investItem.loan_ref].$el.value = investItem.amount
        }
      }, this)
      return investItems
    },
    investCounter()
    {
      return this.$store.getters['invests/investCount']
    },
    totalInvestAmount()
    {
      return this.$store.getters['invests/getTotalInvestAmount']
    }
  },
  mounted()
  {
    window.scrollTo(0, 0)
  },
  watch: {
    '$store.state.investors.investor': function (investor)
    {
      if (investor && investor.investor_available_funds)
      {
        if (parseInt(investor.investor_available_funds) < investConfig.minLimitAmount)
        {
          this.minAmount = parseFloat(investor.investor_available_funds.toFixed(2))
        }
      }
    },
    loaded: function ()
    {
      this.isLoading = !this.loaded
    }
  },
  methods: {
    isNumber: function (evt)
    {
      evt = (evt) || window.event
      const charCode = (evt.which) ? evt.which : evt.keyCode
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46)
      {
        evt.preventDefault()
      }
      else
      {
        return true
      }
    },
    updateInvest(item)
    {
      if (this.validate(this.updatedInvests[item.loan_ref], item.loan_ref, item.investable_amount))
      {
        item.amount = this.updatedInvests[item.loan_ref]
        this.$store.dispatch('invests/updateInvestItem', item)
      }
    },
    removeInvest(itemId)
    {
      this.$store.dispatch('invests/removeInvestItem', itemId)
    },
    removeAll()
    {
      this.$bvModal.msgBoxConfirm(' ', {
        modalClass: 'modal-confirmation-simple',
        title: 'Are you sure?',
        centered: true
      })
        .then(value => {
          if (value) {
            trackingHelper.pushToDataLayer('removeFromCart', this.$store.getters['invests/getInvestItems'])
            this.$store.dispatch('invests/removeAll')
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    validate(event, itemId, maxInvestableAmount)
    {
      const maxInvestAmount = parseFloat(maxInvestableAmount)
      if (!event || event < this.minAmount || event > maxInvestAmount || (maxInvestAmount <= 1 && parseFloat(event) !== maxInvestAmount))
      {
        this.$refs[itemId].$el.classList.add('invalid')
        return false
      }
      else
      {
        this.updatedInvests[itemId] = event
        this.$refs[itemId].$el.classList.remove('invalid')
        return true
      }
    },
    submitConfirm()
    {
      // console.log('Run confirmation!')
      this.isLoading = true
      const investItems = this.$store.getters['invests/getInvestItems']
      const investData = []
      if (investItems.length)
      {
        investItems.forEach(function (item)
        {
          const data = {
            loan_ref: item.loan_ref,
            amount: item.amount
          }
          investData.push(data)
        })
        // console.log('Send new investments:', investData)
        const axios = require('axios').default
        axios.post('investment-create', investData).then(response =>
            {
              if (response.data)
              {
                const results = response.data
                this.gotResponse = true
                // request to update of investor_available_funds
                investor.getInvestor().then(response =>
                {
                  if (response.investor_available_funds !== null)
                  {
                    // console.log('Before: ', this.$store.state.investors.investor.investor_available_funds)
                    // console.log('After: ', response.investor_available_funds)
                    this.$store.state.investors.investor.investor_available_funds = response.investor_available_funds
                  }
                })
                for (const index in results.data)
                {
                  const attr = results.data[index]
                  attr.id = index
                  this.responseItems.push(attr)
                }
                this.responseResult = results.overall_message_i18n
                this.responseType = results.overall_status ? results.overall_status : 'success'

                let invested = 0
                let transactionId = 0
                const successInvestItems = []
                investItems.forEach(function (item, index)
                {
                  if (results.data[item.loan_ref] && results.data[item.loan_ref] && results.data[item.loan_ref].investment_id)
                  {
                    transactionId = results.data[item.loan_ref].investment_id
                    if (transactionId)
                    {
                      invested += parseFloat(results.data[item.loan_ref].invested_amount)
                      investItems[index].invested_amount = parseFloat(results.data[item.loan_ref].invested_amount)
                      investItems[index].investment_id = results.data[item.loan_ref].investment_id
                      successInvestItems.push(investItems[index])
                    }
                  }
                })
                if (invested)
                {
                  trackingHelper.pushToDataLayer('purchase', successInvestItems, {transactionId: transactionId, invested: invested})
                }
                this.$store.dispatch('invests/removeAll')
                // this.$router.push('/investments')
              }
              this.isLoading = false
            }
        ).catch((error) =>
        {
          if (error.response && error.response.data && error.response.data.message_i18n)
          {
            this.errors.push(error.response.data.message_i18n)
          }
          else if (error.response && error.response.data && error.response.data.message)
          {
            this.errors.push(this.$t(error.response.data.message_i18n))
          }
          else if (error.response && error.response.data && error.response.data.message)
          {
            this.errors.push(this.$t(error.response.data.message))
          }
          else
          {
            this.errors.push(this.$t(error))
          }
          this.isLoading = false
        })
      }
    },
    getMinAmount(maxInvestableAmount)
    {
      const investableAmount = parseFloat(maxInvestableAmount)
      const minAmount = parseFloat(this.minAmount)
      return (investableAmount > 1 ? minAmount : investableAmount).toFixed(2)
    },
    maxRound(currentValue, maxValue, refId)
    {
      const diff = maxValue - currentValue
      if (diff < 1)
      {
        // console.log('DIFF:', diff, ' SET to ', maxValue, ' refId ', refId, ' Prev value', this.$refs[refId].$el.value)
        this.$refs[refId].$el.value = maxValue
        return maxValue
      }
      if (parseFloat(currentValue) === 0)
      {
        currentValue = this.minAmount
      }
      return currentValue
    }
  }
}
</script>

<style lang="scss" scoped>
.confirm-invest-table {
  overflow-x: auto;
  overflow-y: hidden;
  font-size: 11px !important;

  .alert {
    margin-bottom: 0 !important;
  }
}

.flag {
  width: 16px;
}

.table-header {
  font-style: normal;
  font-weight: bold;
  font-size: 8px;
  line-height: 10px;
  text-align: center;
  text-transform: uppercase;
  color: #9DA0A6;
}

.invest-ig {
  border: 1px solid #E2F3FF;
  border-radius: 16px;
  padding: 2px;
  font-style: normal;
  font-weight: normal;
  line-height: 20px;
  //margin: 15px 2px 2px;
  /*background: #E2F3FF;*/
  font-size: 19px;
  height: 42px;
  box-shadow: 1px 3px 16px 0 var(--blue-income-50);
  @media screen and (max-width: 1024px)  {
    min-width: 130px;
  }

  .prepend {
    margin-left: 10px;
    padding-top: 8px;
    color: var(--blue-income);
  }

  .invest-input, .invest-input:focus, .invest-input:hover {
    border: none;
    border-radius: 0;
    background: transparent;
    outline: 0;
    box-shadow: none;
    font-size: 19px;
    font-weight: bold;
    text-align: center;
    padding-bottom: 10px;
  }

  .append {
    float: right;
    cursor: pointer;
    margin-right: 8px;
    margin-top: 10px;

    img {
      height: 19px;
    }
  }
}
.remove-invest {
  background: transparent;
  border: none;
  float: right;
  cursor: pointer;
  padding: 1px;
  margin-left: 15px;
  margin-right: 5px;
  margin-top: 2px;
}

.invest-ig {
  display: inline-flex;

  .invalid {
    color: red;
    font-weight: bold;
  }
}

.invest-section {
  min-width: 85px;
}

.summary {
  float: right;
  width: 100%;
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 20px;
  color: #000000;

  .details {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 400;
    font-size: 16px;
    line-height: 150%;
    color: #6D818F;
    width: 100%;

    .value {
      float: right;
      font-weight: 600;
      font-size: 16px;
      line-height: 150%;
      text-align: right;
      color: #0C2D45;
    }
  }

  .confirm-invest-btn {
    background: #30A7FE;
    border-radius: 24px;
    padding-top: 12px;
    margin-left: 20px;
    padding-bottom: 10px;
    border-color: transparent;
    font-weight: bold;
    color: white;
    max-width: 270px;
    width: 100%;
    margin-bottom: 10px;
    @media screen and (max-width: 1240px) {
      max-width: 100%;
      display: block;
      margin-left: 0;
    }
  }

  .remove-all {
    font-family: 'Open Sans';
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 25px;
    text-align: center;
    letter-spacing: 1px;
    color: #30A7FE;
    max-width: 260px;
    width: 100%;
    border: 1px solid #30A7FE;
    box-sizing: border-box;
    border-radius: 16px;
    background: transparent;
    margin-bottom: 10px;
    @media screen and (max-width: 1240px) {
      max-width: 100%;
      display: block;
    }
  }

  .confirm-invest-survey-btn-container {
    margin-top: 20px;
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .confirm-invest-survey {
    font-size: 14px;
  }
}

.small-notification-text {
  margin-top: -8px;
  color: #6e777a;
  font-size: 14px;

  &-closer {
    margin-top: 0;
  }
}

@media only screen and (max-width: 360px) {
  .summary {
    .confirm-invest-survey-btn-container {
      justify-content: center;
    }

    .confirm-invest-btn {
      margin-left: 0;
      margin-top: 12px;
    }
  }
}

</style>
